'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Search, X } from 'lucide-react';
import { fetchResultadosPublicos, type ResultadoPublicoApi } from '@/services/indicadores';
import { fetchCategorias, type CategoriaApi } from '@/services/catalogos';

// Datos mockeados extraídos del diseño
const CATEGORIES_META: Record<string, { topColor: string, pillBg: string, pillText: string }> = {
  'Actividad Económica': { topColor: 'bg-[#3B82F6]', pillBg: 'bg-[#EFF6FF]', pillText: 'text-[#1D4ED8]' },
  'Economia': { topColor: 'bg-[#3B82F6]', pillBg: 'bg-[#EFF6FF]', pillText: 'text-[#1D4ED8]' },
  'Infraestructura': { topColor: 'bg-[#8B5CF6]', pillBg: 'bg-[#F5F3FF]', pillText: 'text-[#6D28D9]' },
  'Ambiente': { topColor: 'bg-[#10B981]', pillBg: 'bg-[#ECFDF5]', pillText: 'text-[#047857]' },
  'Salud': { topColor: 'bg-[#F43F5E]', pillBg: 'bg-[#FFF1F2]', pillText: 'text-[#BE123C]' },
  'Educación': { topColor: 'bg-[#F59E0B]', pillBg: 'bg-[#FFFBEB]', pillText: 'text-[#B45309]' },
  'Educacion': { topColor: 'bg-[#F59E0B]', pillBg: 'bg-[#FFFBEB]', pillText: 'text-[#B45309]' },
  'Gobernanza': { topColor: 'bg-[#64748B]', pillBg: 'bg-[#F8FAFC]', pillText: 'text-[#334155]' },
  'Servicios Públicos': { topColor: 'bg-[#0EA5E9]', pillBg: 'bg-[#F0F9FF]', pillText: 'text-[#0369A1]' },
  'Energia': { topColor: 'bg-[#0EA5E9]', pillBg: 'bg-[#F0F9FF]', pillText: 'text-[#0369A1]' },
  'Movilidad': { topColor: 'bg-[#0EA5E9]', pillBg: 'bg-[#F0F9FF]', pillText: 'text-[#0369A1]' },
  'Vivienda': { topColor: 'bg-[#8B5CF6]', pillBg: 'bg-[#F5F3FF]', pillText: 'text-[#6D28D9]' },
  'Poblacion y territorio': { topColor: 'bg-[#64748B]', pillBg: 'bg-[#F8FAFC]', pillText: 'text-[#334155]' },
};

const ALL_CATEGORIES = [
  'Actividad Económica', 'Infraestructura', 'Ambiente', 'Salud', 'Educación', 'Gobernanza', 'Servicios Públicos'
];

export default function PublicPortalPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [indicators, setIndicators] = useState<ResultadoPublicoApi[]>([]);
  const [categories, setCategories] = useState<CategoriaApi[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        const [cats, res] = await Promise.all([
          fetchCategorias(),
          fetchResultadosPublicos({ per_page: 20 })
        ]);
        if (active) {
          if (cats.length > 0) setCategories(cats);
          if (res.length > 0) setIndicators(res);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load dynamic data', err);
        if (active) setLoading(false);
      }
    };
    loadData();
    return () => { active = false; };
  }, []);

  const displayIndicators = indicators.length > 0
    ? indicators.map((ind) => ({
        id: ind.codigo_interno || String(ind.corrida_id),
        title: ind.indicador,
        category: ind.categoria,
        value: typeof ind.valor_resultado === 'number'
          ? ind.valor_resultado.toLocaleString('es-AR')
          : ind.valor_resultado,
        unit: `${ind.unidad_simbolo || ind.unidad_medida} · ${ind.periodo_referencia ? new Date(ind.periodo_referencia).getFullYear() : '2022'}`
      }))
    : [];

  const displayCategories = categories.length > 0
    ? categories.map(c => c.nombre)
    : ALL_CATEGORIES;

  // Lógica de filtrado
  const filteredIndicators = displayIndicators.filter((ind) => {
    const matchesCategory = activeCategory ? ind.category === activeCategory : true;
    const matchesSearch = searchTerm.trim() === '' ||
      ind.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ind.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const toggleCategory = (cat: string) => {
    setActiveCategory(prev => prev === cat ? null : cat);
  };

  return (
    <>      {/* 2. Hero Section */}
      <section className="bg-[#003876] text-white px-8 py-16 relative overflow-hidden">
        {/* Decorative solid circle as in mockup */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#002B5B] rounded-full translate-x-1/3 -translate-y-1/4 pointer-events-none"></div>
        
        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-4xl md:text-5xl font-[family-name:var(--font-righteous)] mb-4 leading-tight tracking-wide">
            Sistema de Indicadores de Ciudad Inteligente y Sostenible
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-2 font-light">
            Municipio de Luján · Acceso público y abierto a los indicadores estratégicos de la ciudad
          </p>
          <p className="text-sm text-[#FDE047] font-medium tracking-wide mb-10">
            Conforme ISO 37120 / IRAM-ISO 37120 · Alianza Universitaria Argentina-Europea
          </p>

          {/* Search Bar - Connected style */}
          <div className="flex flex-col sm:flex-row mb-8 max-w-3xl">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar indicador por nombre o categoría..."
                className="w-full bg-white pl-12 pr-10 py-4 rounded-l-xl sm:rounded-r-none rounded-r-xl text-gray-900 focus:outline-none text-lg shadow-lg"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <button className="bg-[#FDE047] hover:bg-[#FACC15] text-[#0B2A56] font-bold text-lg px-10 py-4 sm:rounded-l-none rounded-r-xl sm:rounded-r-xl rounded-l-xl mt-2 sm:mt-0 transition-all shadow-lg active:scale-95">
              Buscar
            </button>
          </div>

          {/* Category Pills - Solid Blue as in mockup */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-white/70 mr-2">Explorar por categoría:</span>
            {displayCategories.map(cat => (
              <button 
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm transition-all border ${
                  activeCategory === cat 
                    ? 'bg-[#FDE047] text-[#0B2A56] font-bold border-[#FDE047] shadow-md' 
                    : 'bg-[#1A4B8C] text-white border-transparent hover:bg-[#2563EB]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Indicadores Vigentes Section */}
      <section className="bg-white px-8 py-16 flex-1">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 flex items-center justify-between">
            <div>
              <h3 className="text-3xl font-[family-name:var(--font-righteous)] text-[#002B5B] mb-2 inline-block border-b-4 border-[#3B82F6] pb-1">
                Indicadores Vigentes
              </h3>
              <p className="text-gray-500 text-sm mt-2">
                Última corrida aprobada: Período 2022 · {filteredIndicators.length} indicadores encontrados
              </p>
            </div>
            
            {/* Show active filters summary */}
            {(activeCategory || searchTerm) && (
              <div className="hidden md:flex items-center gap-2">
                <span className="text-sm text-gray-400">Filtros activos:</span>
                {activeCategory && (
                  <span className="px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold rounded-md flex items-center gap-1">
                    {activeCategory}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setActiveCategory(null)} />
                  </span>
                )}
                {searchTerm && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-md flex items-center gap-1">
                    "{searchTerm}"
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchTerm('')} />
                  </span>
                )}
              </div>
            )}
          </div>

          {filteredIndicators.length === 0 ? (
            <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-500 mb-2">No se encontraron indicadores</h4>
              <p className="text-gray-400">Intenta probar con otros términos de búsqueda o selecciona otra categoría.</p>
              <button 
                onClick={() => { setSearchTerm(''); setActiveCategory(null); }}
                className="mt-6 text-blue-600 font-bold hover:underline"
              >
                Limpiar filtros
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredIndicators.map((ind) => {
                const meta = CATEGORIES_META[ind.category] || CATEGORIES_META['Gobernanza'];
                
                return (
                  <div key={ind.id} className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.06)] border border-gray-100 p-6 relative overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.1)] transition-all flex flex-col group">
                    <div className={`absolute top-0 left-0 right-0 h-1.5 ${meta.topColor}`}></div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4 self-start ${meta.pillBg} ${meta.pillText}`}>
                      {ind.category}
                    </span>
                    <h4 className="font-bold text-gray-800 text-lg mb-6 leading-tight min-h-[48px]">{ind.title}</h4>
                    <div className="mb-6 flex-1">
                      <div className="text-4xl font-[family-name:var(--font-righteous)] text-[#003876] tracking-wide mb-1">{ind.value}</div>
                      <div className="text-xs text-gray-400 font-medium">{ind.unit}</div>
                    </div>
                    <Link href={`/indicadores/${ind.id}`} className="bg-[#F8FAFC] hover:bg-[#E2E8F0] text-[#0F172A] text-sm font-bold py-2 px-4 rounded-lg w-full text-left transition-colors flex items-center gap-2">
                      Ver detalle <span className="opacity-60 group-hover:translate-x-1 transition-transform">→</span>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* 4. Data Access Section */}
      <section className="bg-[#F3F4F6] px-8 py-16">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-2xl font-[family-name:var(--font-righteous)] text-[#002B5B] mb-2">Acceso Abierto a los Datos</h3>
            <p className="text-gray-500">Descargá los datasets completos en formatos abiertos (CSV / PDF)</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <a 
              href="/api/externo/resultados-publicos?format=csv"
              className="bg-[#0052A3] hover:bg-[#003876] text-white font-bold py-3 px-8 rounded-lg shadow transition-colors inline-block text-center"
            >
              Descargar CSV
            </a>
            <button 
              onClick={() => window.print()}
              className="bg-white hover:bg-gray-50 text-[#0052A3] font-bold py-3 px-8 rounded-lg shadow-sm border border-gray-200 transition-colors"
            >
              Descargar PDF
            </button>
            <span className="text-xs text-gray-400 font-medium ml-2 hidden lg:inline-block">
              Sin autenticación requerida · Acceso 100% público
            </span>
          </div>
        </div>
      </section>
    </>
  );
}
